import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Box,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockStrengthIcon,
  CheckCircleOutline as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';

import { getDriveApiToken } from '../../lib/auth-token';
const api = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, ''),
});

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = getDriveApiToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Password strength criteria
const passwordCriteria = [
  { id: 'length', description: 'At least 8 characters', check: (p: string) => p.length >= 8 },
  { id: 'uppercase', description: 'Includes uppercase letter', check: (p: string) => /[A-Z]/.test(p) },
  { id: 'lowercase', description: 'Includes lowercase letter', check: (p: string) => /[a-z]/.test(p) },
  { id: 'number', description: 'Includes number', check: (p: string) => /[0-9]/.test(p) },
  { id: 'special', description: 'Includes special character', check: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export interface SetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSetPassword: (password: string) => Promise<void>;
  isReset: boolean;
}

export const SetPasswordDialog: React.FC<SetPasswordDialogProps> = ({
  open,
  onClose,
  onSetPassword,
  isReset = false,
}) => {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const validatePassword = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    if (password !== passwordConfirm) {
      setError('Passwords do not match');
      return false;
    }
    
    setError(null);
    return true;
  };
  
  const handleSubmit = async () => {
    if (!validatePassword()) {
      return;
    }
    
    try {
      setLoading(true);
      await onSetPassword(password);
      setPassword('');
      setPasswordConfirm('');
      onClose();
    } catch (error) {
      setError('Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setPassword('');
    setPasswordConfirm('');
    setError(null);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="set-password-dialog-title"
    >
      <DialogTitle id="set-password-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            {isReset ? 'Reset Password Protection' : 'Set Password Protection'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            {isReset 
              ? 'Create a new password for this shared resource.' 
              : 'Protect this shared resource with a password.'}
          </Typography>
          
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            fullWidth
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            margin="normal"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading || !password || !passwordConfirm}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Setting Password...' : 'Set Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface PasswordVerifyDialogProps {
  open: boolean;
  onClose: () => void;
  onPasswordVerified: (password: string) => void;
  shareToken: string;
}

export const PasswordVerifyDialog: React.FC<PasswordVerifyDialogProps> = ({
  open,
  onClose,
  onPasswordVerified,
  shareToken,
}) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const handleVerify = async () => {
    if (!password) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await api.post(`/shares/public/${shareToken}/verify-password`, {
        password,
      });
      
      onPasswordVerified(password);
      setPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Incorrect password. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon color="primary" />
        Password Required
      </DialogTitle>
      
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" paragraph>
          This content is password protected. Please enter the password to access it.
        </Typography>
        
        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && password) {
              handleVerify();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleVerify}
          disabled={!password || loading}
        >
          Verify Password
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 